import React, { useState, useContext, useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common } from "../util/common";
import { ChatContext } from "../context/contexts";
import { highlightText, convertTo12HourPlusDateFormat } from "../util/helperFunctions";
import ModalShell from "./modal/ModalShell";
import UserAvatar from "./modal/UserAvatar";
import { EmptyState, SkeletonRows } from "./modal/EmptyState";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;
const SCROLL_TARGET_ID = "search-chat-scrollable-body";

function SearchChatModal({ conversationData, showHideSearchmodal }) {
  const Chat = useContext(ChatContext);
  const conversation_id = conversationData?.id;
  const conversation_name = conversationData?.name;
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchedTerm, setSearchedTerm] = useState(""); // term the current results belong to
  const [loading, setLoading] = useState(false);
  const [jumpingId, setJumpingId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const requestRef = useRef(0);

  // pageToLoad 1 replaces the results, later pages append to them
  const runSearch = async (term, pageToLoad = 1) => {
    const keyword = term.trim();
    const append = pageToLoad > 1;
    const requestId = ++requestRef.current; // ignore out-of-order responses
    if (keyword.length < MIN_CHARS) {
      setSearchResults([]);
      setSearchedTerm("");
      setHasMore(false);
      setTotalRecords(0);
      setPage(1);
      setLoading(false);
      setIsFetchingMore(false);
      return;
    }
    if (append) setIsFetchingMore(true);
    else setLoading(true);
    try {
      const queryResults = await ApiService.message.searchMessageV2(conversation_id, {
        keyword,
        page: pageToLoad,
        limit: PAGE_SIZE,
      });
      if (requestId !== requestRef.current) return;
      const { success, result, pagination } = queryResults || {};
      if (!success) showSwal(common.errors.SEARCH_MESSAGE_ERROR);
      const rows = Array.isArray(result) ? result : [];
      setSearchResults((prev) => {
        if (!append) return rows;
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...rows.filter((r) => !seen.has(r.id))];
      });
      setPage(pageToLoad);
      setHasMore(!!pagination?.hasNextPage);
      setTotalRecords(pagination?.totalRecords ?? rows.length);
      setSearchedTerm(keyword);
    } catch (error) {
      if (requestId !== requestRef.current) return;
      console.error(error);
      showSwal(common.errors.SEARCH_MESSAGE_ERROR);
      if (!append) {
        setSearchResults([]);
        setHasMore(false);
        setTotalRecords(0);
      }
      setSearchedTerm(keyword);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setIsFetchingMore(false);
      }
    }
  };

  const fetchMoreData = () => {
    if (!isFetchingMore && !loading && hasMore) runSearch(searchedTerm || searchInput, page + 1);
  };

  // live search while typing
  useEffect(() => {
    const t = setTimeout(() => runSearch(searchInput), DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleResultClick = async (chatId) => {
    if (jumpingId) return;
    setJumpingId(chatId);
    try {
      const messagePositionResponse = await ApiService.message.getMessagePosition(conversation_id, chatId);
      const { success, response } = messagePositionResponse;

      if (!success) {
        showSwal(common.errors.FETCH_MSG_POSITION_ERROR);
        return;
      }
      const { thread, query } = response;
      const { thread_reply, message_id } = thread;

      // close so the message is visible (especially on mobile)
      showHideSearchmodal();

      if (thread_reply) {
        Chat.handleThreadViewMsgId(message_id);
      } else {
        Chat.reSetConversationDetail(conversation_id, query, function callback() {
          setTimeout(() => {
            const msgView = document.getElementById(chatId);
            if (msgView) {
              const coloredElement = msgView.querySelector("span");
              coloredElement?.classList.add("pulse");

              msgView.scrollIntoView({ behavior: "smooth" });

              setTimeout(() => {
                coloredElement?.classList.remove("pulse");
              }, 2500);
            }
          }, 1000);
        });
      }
    } catch (error) {
      console.error(error);
      setJumpingId(null);
    }
  };

  const trimmed = searchInput.trim();
  const tooShort = trimmed.length < MIN_CHARS;

  const header = (
    <div className='bh-search bh-search--round'>
      <i className='fa fa-search bh-search__icon' aria-hidden='true'></i>
      <input
        type='text'
        data-autofocus
        className='bh-input bh-search__input'
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            runSearch(searchInput);
          }
        }}
        placeholder='Search messages…'
        aria-label='Search messages'
        autoComplete='off'
      />
      {searchInput && (
        <button
          type='button'
          className='bh-icon-btn bh-search__clear'
          onClick={() => setSearchInput("")}
          aria-label='Clear search'
        >
          <i className='fa fa-times-circle' aria-hidden='true'></i>
        </button>
      )}
    </div>
  );

  let content;
  if (tooShort) {
    content = (
      <EmptyState
        icon='fa-comments-o'
        title={`Search messages in ${conversation_name || "this conversation"}`}
        hint={`Type at least ${MIN_CHARS} characters`}
      />
    );
  } else if (loading && searchResults.length === 0) {
    content = <SkeletonRows count={5} />;
  } else if (!loading && searchedTerm === trimmed && searchResults.length === 0) {
    content = <EmptyState title={`No messages match “${trimmed}”`} hint='Try a different keyword' />;
  } else {
    content = (
      <>
        <p className='bh-month'>
          {totalRecords} result{totalRecords === 1 ? "" : "s"}
          {loading ? " · updating…" : ""}
        </p>
        <InfiniteScroll
          dataLength={searchResults.length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollableTarget={SCROLL_TARGET_ID}
          scrollThreshold='180px'
          style={{ overflow: "visible" }}
          loader={<SkeletonRows count={2} />}
        >
        <ul className='bh-list'>
          {searchResults.map((result) => (
            <li key={result?.id}>
              <button
                type='button'
                className={`bh-row bh-row--click ${jumpingId === result?.id ? "bh-row--busy" : ""}`}
                onClick={() => handleResultClick(result?.id)}
              >
                <UserAvatar user={result?.user} size={36} />
                <div className='bh-row__main'>
                  <div className='bh-row__top'>
                    <span className='bh-row__name'>{result?.user?.display_name}</span>
                    {result?.createdAt && (
                      <span className='bh-row__time'>{convertTo12HourPlusDateFormat(result.createdAt)}</span>
                    )}
                  </div>
                  <div className='bh-row__text'>{highlightText(result?.content, searchedTerm || trimmed)}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
        </InfiniteScroll>
      </>
    );
  }

  return (
    <ModalShell
      title='Search messages'
      subtitle={conversation_name}
      size='md'
      onClose={showHideSearchmodal}
      headerExtra={header}
      bodyClassName='bh-modal__body--flush'
      bodyId={SCROLL_TARGET_ID}
    >
      {content}
    </ModalShell>
  );
}

export default SearchChatModal;
